import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const EMPTY = {
  name: "", category: "", description: "",
  deliverables: "", methodology: "",
  typicalPrice: "", typicalDuration: "",
  question: "", answer: "",
  keywords: "", tags: "",
  productId: "",
  status: "DRAFT", published: false, isActive: true,
};
type SolutionForm = typeof EMPTY;

export default function SolutionNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [products, setProducts] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<SolutionForm>(EMPTY);

  useEffect(() => {
    api.get("/products", { params: { pageSize: 100 } }).then((r) => setProducts(r.data.data ?? []));
  }, []);

  function set<K extends keyof SolutionForm>(k: K, v: SolutionForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save(mode: "save" | "saveAndNew") {
    setBusy(true); setError(null);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === null || v === undefined) continue;
        if (k === "keywords" || k === "tags") payload[k] = (v as string).split(",").map((s) => s.trim()).filter(Boolean);
        else if (k === "typicalPrice") payload[k] = Number(v);
        else payload[k] = v;
      }
      await api.post("/solutions", payload);
      qc.invalidateQueries({ queryKey: ["/solutions"] });
      if (mode === "saveAndNew") setForm(EMPTY);
      else navigate("/solutions");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create solution.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader
        title="Create IT Solution"
        description="Add a new IT service offering or knowledge-base entry."
      />
      <form onSubmit={(e) => { e.preventDefault(); save("save"); }} className="p-6">
        <div className="mx-auto max-w-3xl space-y-4">
          <Section title="Identity">
            <Field id="name" label="Solution title" required className="md:col-span-2">
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Field>
            <Field id="category" label="Category">
              <Select id="category" value={form.category} onChange={(e) => set("category", e.target.value)}>
                <option value="">—</option>
                <option value="Data & AI">Data & AI</option>
                <option value="Cloud">Cloud</option>
                <option value="Staffing">Staffing</option>
                <option value="Government">Government</option>
                <option value="Delivery">Delivery</option>
                <option value="Knowledge Base">Knowledge Base</option>
                <option value="Other">Other</option>
              </Select>
            </Field>
            <Field id="productId" label="Related product">
              <Select id="productId" value={form.productId} onChange={(e) => set("productId", e.target.value)}>
                <option value="">— None —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field id="status" label="Status">
              <Select id="status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="DRAFT">Draft</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="APPROVED">Approved</option>
                <option value="PUBLISHED">Published</option>
                <option value="ARCHIVED">Archived</option>
              </Select>
            </Field>
            <div className="flex items-center gap-2">
              <input id="published" type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} />
              <label htmlFor="published" className="text-sm font-medium">Published (visible to clients)</label>
            </div>
            <div className="flex items-center gap-2">
              <input id="isActive" type="checkbox" checked={form.isActive} onChange={(e) => set("isActive", e.target.checked)} />
              <label htmlFor="isActive" className="text-sm font-medium">Active</label>
            </div>
          </Section>

          <Section title="Service catalog details">
            <Field id="typicalPrice" label="Typical price (USD)">
              <Input id="typicalPrice" type="number" min="0" value={form.typicalPrice} onChange={(e) => set("typicalPrice", e.target.value)} />
            </Field>
            <Field id="typicalDuration" label="Typical duration">
              <Input id="typicalDuration" placeholder="8–12 weeks, ongoing retainer…" value={form.typicalDuration} onChange={(e) => set("typicalDuration", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <label htmlFor="description" className="text-sm font-medium">Description</label>
              <textarea
                id="description"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="deliverables" className="text-sm font-medium">Deliverables</label>
              <textarea
                id="deliverables"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Bulleted list or free text"
                value={form.deliverables}
                onChange={(e) => set("deliverables", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="methodology" className="text-sm font-medium">Methodology / Approach</label>
              <textarea
                id="methodology"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Phases, team composition, deliverables cadence…"
                value={form.methodology}
                onChange={(e) => set("methodology", e.target.value)}
              />
            </div>
          </Section>

          <Section title="Knowledge base (optional)">
            <div className="md:col-span-2">
              <label htmlFor="question" className="text-sm font-medium">Question</label>
              <textarea
                id="question"
                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.question}
                onChange={(e) => set("question", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="answer" className="text-sm font-medium">Answer</label>
              <textarea
                id="answer"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.answer}
                onChange={(e) => set("answer", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="keywords" className="text-sm font-medium">Keywords (comma-separated)</label>
              <Input id="keywords" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="tags" className="text-sm font-medium">Tags (comma-separated)</label>
              <Input id="tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={!!form.name}
            onCancel={() => navigate("/solutions")}
            onSaveAndNew={() => save("saveAndNew")}
            onSubmit={() => save("save")}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
